/**
 * The Layout choice (user menu → Layout): one card per layout the application
 * offers, each with a small schematic of its idiom. Switching is instant —
 * open screens, Pinned, Recent and Open editors all live in the shared model.
 */
import { Check, X } from 'lucide-react'
import { useEffect, useRef, type KeyboardEvent, type ReactElement } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../../../testHooks'
import type { LayoutDescriptor, LayoutId } from '../config'
import { S, iconButton } from '../tokens'

/** A schematic of each layout's chrome (decorative: the label names it). */
export function LayoutThumb({ id, size = 112 }: { id: LayoutId; size?: number }) {
  const h = Math.round(size * 0.62)
  const bar = 'var(--color-border-strong, #c7cfd9)'
  const acc = 'var(--color-accent, #0061D0)'
  const fill = 'var(--color-surface-2, #f0f3f7)'
  const paper = 'var(--color-bg-secondary, #ffffff)'
  const r = (x: number, y: number, w: number, hh: number, f = bar, rx = 1.5) => (
    <rect x={x} y={y} width={w} height={hh} rx={rx} fill={f} />
  )
  const shapes: Record<LayoutId, ReactElement> = {
    classic: <>{r(0, 0, 100, 8)}{r(0, 8, 26, 54, fill, 0)}{r(4, 13, 18, 3)}{r(6, 19, 16, 3)}{r(6, 25, 14, 3, acc)}{r(4, 31, 18, 3)}{r(30, 13, 66, 6)}{r(30, 22, 66, 34, paper)}</>,
    rail: <>{r(0, 0, 100, 8)}{r(0, 8, 9, 54, acc, 0)}{r(9, 8, 22, 54, fill, 0)}{r(12, 13, 16, 3)}{r(12, 19, 16, 3)}{r(35, 13, 61, 6)}{r(35, 22, 61, 34, paper)}{r(35, 58, 30, 3, acc)}</>,
    megamenu: <>{r(0, 0, 100, 8)}{r(0, 8, 100, 7, fill, 0)}{r(6, 10, 12, 3, acc)}{r(22, 10, 12, 3)}{r(38, 10, 12, 3)}{r(54, 10, 12, 3)}{r(6, 18, 46, 22, paper)}{r(9, 21, 18, 3)}{r(9, 27, 14, 3)}{r(30, 21, 18, 3)}{r(4, 44, 92, 16, fill)}</>,
    launchpad: <>{r(0, 0, 100, 8)}{r(0, 8, 100, 7, fill, 0)}{r(4, 9, 16, 6, paper)}{r(22, 9, 16, 6, acc)}{r(40, 9, 16, 6, paper)}{[0, 1, 2, 3].map((i) => <g key={i}>{r(8 + i * 22, 22, 16, 14, paper)}{r(8 + i * 22, 40, 16, 14, paper)}</g>)}</>,
    'desktop-mac': <>{r(0, 0, 100, 6, fill, 0)}{r(4, 1.5, 6, 3, acc)}{r(14, 1.5, 8, 3)}{r(26, 1.5, 8, 3)}{r(10, 11, 62, 36, paper)}{r(10, 11, 62, 5)}{r(30, 20, 56, 30, paper)}{r(30, 20, 56, 5)}{r(22, 54, 56, 7, fill, 3)}{[0, 1, 2, 3, 4].map((i) => r(26 + i * 10, 55.5, 6, 4, i === 1 ? acc : bar, 1))}</>,
    flat: <>{r(0, 0, 100, 8)}{r(0, 8, 30, 54, fill, 0)}{r(4, 12, 14, 3, acc)}{r(4, 18, 22, 5, paper)}{r(4, 25, 22, 5, paper)}{r(4, 32, 22, 5, paper)}{r(34, 12, 40, 3)}{r(34, 18, 62, 38, paper)}</>,
    office: <>{r(0, 0, 100, 6, acc, 0)}{r(0, 6, 100, 6, fill, 0)}{r(4, 7.5, 10, 3)}{r(16, 7.5, 10, 3, acc)}{r(28, 7.5, 10, 3)}{r(0, 12, 100, 12, paper, 0)}{[0, 1, 2, 3, 4, 5].map((i) => r(4 + i * 15, 14, 11, 8, fill))}{r(4, 27, 92, 28, paper)}{r(4, 56, 18, 5, acc)}{r(24, 56, 18, 5)}</>,
  }
  return (
    <svg aria-hidden width={size} height={h} viewBox="0 0 100 62" style={{
      display: 'block', borderRadius: 6, background: 'var(--color-bg, #eef1f5)', border: `1px solid ${S.border}`,
    }}>
      {shapes[id]}
    </svg>
  )
}

export function LayoutPickerDialog({ value, options, onChange, onClose }: {
  value: LayoutId
  options: LayoutDescriptor[]
  onChange: (id: LayoutId) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(typeof document !== 'undefined' ? document.activeElement as HTMLElement : null)
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus()
    const back = returnTo.current
    return () => { back?.focus?.() }
  }, [])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return }
    const radios = [...(ref.current?.querySelectorAll<HTMLElement>('[role="radio"]') || [])]
    const i = radios.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = radios[(i + step + radios.length) % radios.length]
    next?.focus()
    const id = next?.dataset.layout as LayoutId | undefined
    if (id) onChange(id)
  }
  if (typeof document === 'undefined') return null
  return createPortal(
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 9460, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="tw-sh-layout-h" onKeyDown={onKey}
        data-tw-dialog="shell-layout"
        style={{ width: 'min(760px, 100%)', maxHeight: '90vh', overflow: 'auto', borderRadius: 14,
          background: S.raised, color: S.text, border: `1px solid ${S.border}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)', padding: 18, fontFamily: S.font }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h2 id="tw-sh-layout-h" style={{ margin: 0, fontSize: 17, fontWeight: 600, flex: 1 }}>Layout</h2>
          <button type="button" className="tw-sh-btn" onClick={onClose} aria-label="Close" style={iconButton(34)}>
            <X size={16} aria-hidden />
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 14, color: S.text2 }}>
          How the application arranges its menus. Your screens, pinned and recent screens and open editors stay as they are.
        </p>
        <div role="radiogroup" aria-label="Layout" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10,
        }}>
          {options.map((o) => {
            const on = o.id === value
            return (
              <button key={o.id} type="button" role="radio" aria-checked={on} data-layout={o.id}
                tabIndex={on ? 0 : -1} onClick={() => onChange(o.id)}
                {...tw('action', `shell-layout:${o.id}`, { selected: on })}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8, padding: 10, textAlign: 'left',
                  borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', color: S.text,
                  background: on ? S.accentWeak : 'transparent',
                  border: `${on ? 2 : 1}px solid ${on ? S.accent : S.border}`,
                }}>
                <LayoutThumb id={o.id} size={136} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 600 }}>
                  {on && <Check size={15} color={S.accent} aria-hidden />}{o.label}
                </span>
                <span style={{ fontSize: 13, color: S.text2, lineHeight: 1.4 }}>{o.description}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
